import { createRouter, createWebHistory } from 'vue-router'
import { getCurrentUser } from '@/firebase/firebase-auth'
import HomeView from '../views/HomeView.vue'
// Import specific functions directly
import { isProfileComplete, isAdmin } from '@/firebase/profile-service'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    // Public routes
    {
      path: '/',
      name: 'home',
      component: HomeView,
    },
    {
      path: '/about',
      name: 'About',
      component: () => import('../views/AboutView.vue'),
    },
    {
      path: '/how-it-works',
      name: 'HowItWorks',
      component: () => import('../views/HowItWorksView.vue'),
    },
    {
      path: '/find-registry',
      name: 'FindRegistry',
      component: () => import('../views/FindRegistryView.vue'),
    },
    {
      path: '/donate',
      name: 'Donate',
      component: () => import('../views/DonateView.vue'),
    },
    {
      path: '/ways-to-help',
      name: 'WaysToHelp',
      component: () => import('../views/WaysToHelpView.vue'),
    },
    {
      path: '/igive',
      name: 'IGive',
      component: () => import('../views/IGiveView.vue'),
    },
    {
      path: '/auth',
      children: [
        {
          path: 'sign-in',
          name: 'SignIn',
          component: () => import('../views/Authentication/SignInView.vue'),
          meta: {
            requiresGuest: true
          }
        },
        {
          path: 'sign-up',
          name: 'SignUp',
          component: () => import('../views/Authentication/SignUpView.vue'),
          meta: {
            requiresGuest: true
          }
        },
        {
          path: 'reset-password',
          name: 'ResetPassword',
          component: () => import('../views/Authentication/ResetPasswordView.vue'),
          meta: {
            requiresGuest: true
          }
        }
      ]
    },
    {
      path: '/dashboard',
      name: 'dashboard',
      component: () => import('../views/DashboardView.vue'),
      meta: {
        requiresAuth: true,
        checkProfile: true
      }
    },
    {
      path: '/profile',
      name: 'profile',
      component: () => import('../views/ProfileView.vue'),
      meta: {
        requiresAuth: true
      }
    },

    // Admin routes
    {
      path: '/admin',
      name: 'Admin',
      component: () => import('../views/admin/AdminView.vue'),
      meta: {
        requiresAuth: true,
        requiresAdmin: true
      }
    },

    // Registry routes
    {
      path: '/create-registry',
      name: 'CreateRegistry',
      component: () => import('../views/CreateRegistryView.vue'),
      meta: {
        requiresAuth: true,
        checkProfile: true
      }
    },
    {
      path: '/registry/:id',
      name: 'ViewRegistry',
      component: () => import('../views/ViewRegistryView.vue')
    },
    {
      path: '/registry/url/:url',
      name: 'ViewRegistryByUrl',
      component: () => import('../views/ViewRegistryView.vue')
    },
    {
      path: '/registry/:id/edit',
      name: 'EditRegistry',
      component: () => import('../views/EditRegistryView.vue'),
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/registry/:id/add-item',
      name: 'AddRegistryItem',
      component: () => import('../views/AddRegistryItemView.vue'),
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/registry/:id/edit-item/:itemId',
      name: 'EditRegistryItem',
      component: () => import('../views/AddRegistryItemView.vue'),
      meta: {
        requiresAuth: true
      }
    }
  ],
})

// Navigation guard for authentication
router.beforeEach(async (to, from, next) => {
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth);
  const requiresGuest = to.matched.some(record => record.meta.requiresGuest);
  const checkProfile = to.matched.some(record => record.meta.checkProfile);
  const requiresAdmin = to.matched.some(record => record.meta.requiresAdmin);
  
  try {
    const currentUser = await getCurrentUser();
    
    if (requiresAuth && !currentUser) {
      // Redirect to login if trying to access protected route without authentication
      next({ name: 'SignIn' });
    } else if (requiresGuest && currentUser) {
      // Redirect to dashboard if trying to access guest route while authenticated
      next({ name: 'dashboard' });
    } else if (currentUser && requiresAdmin) {
      const userIsAdmin = await isAdmin(currentUser.uid);
      if (!userIsAdmin) {
        next({ name: 'dashboard' });
      } else {
        next();
      }
    } else if (currentUser && checkProfile) {
      // Check if user profile is complete
      try {
        const isComplete = await isProfileComplete(currentUser.uid);
        
        // If profile is not complete and not already heading to profile page, redirect to profile
        if (!isComplete && to.name !== 'profile') {
          next({ 
            name: 'profile', 
            query: { 
              newUser: 'true'
            },
            replace: true
          });
        } else {
          // Continue to destination
          next();
        }
      } catch (error) {
        console.error("Error checking profile status:", error);
        next();
      }
    } else {
      // Allow navigation
      next();
    }
  } catch (error) {
    console.error("Authentication error:", error);
    if (requiresAuth) {
      next({ name: 'SignIn' });
    } else {
      next();
    }
  }
});

export default router
